/**
 * Decode a saved text original. Producers record no character set and older publisher files are
 * Windows-1252, so anything that is not valid UTF-8 is read as Windows-1252. NUL characters are
 * dropped, as the search index does.
 */
export function decodeAgencyTextOriginal(bytes: ArrayBuffer): string {
  let text: string
  try {
    text = new TextDecoder('utf-8', { fatal: true }).decode(bytes)
  } catch {
    text = new TextDecoder('windows-1252').decode(bytes)
  }
  return text.replaceAll('\u0000', '')
}

/**
 * Show plain text literally with the file's own line breaks: blank lines separate paragraphs and
 * every other line break stays a line break. Breaks are `<br>` tags because the highlighter rebuilds
 * the page from words and tags, dropping raw newlines and repeated spaces. Nothing is parsed as
 * Markdown or HTML, so footnote markers such as `<1>` and underscores stay as written.
 */
export function plainTextToHtml(text: string): string {
  return text
    .replace(/\r\n?/g, '\n')
    .split(/\n[ \t]*\n/)
    .map((paragraph) =>
      paragraph
        .split('\n')
        .map((line) => escapeHtml(line.trim()))
        .filter((line) => line.length > 0)
        .join('<br>')
    )
    .filter((paragraph) => paragraph.length > 0)
    .map((paragraph) => `<p>${paragraph}</p>`)
    .join('\n')
}

/**
 * Saved passages prepared for highlighting a plain-text original. The highlighter decodes entities
 * once and drops anything shaped like a tag, so a literal `<1>` footnote marker would vanish from
 * the passage while the escaped page still shows it. Escaping twice leaves the passage escaped
 * exactly like the page.
 */
export function plainTextHighlightPassages(passages: string[]): string[] {
  return passages.map((passage) => escapeHtml(escapeHtml(passage)))
}

function escapeHtml(text: string): string {
  return text.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;')
}
